// From: https://gist.github.com/mbaranovski/2dff408e284286d261db763b4b987c4b

import React from 'react';
import PropTypes from 'prop-types';

export function createReducerContext(modelName, reducer, initialState) {
  if (!modelName)
    throw new Error(
      'You need to provide the model name when creating new reducer context!',
    );

  const name = `${modelName[0].toUpperCase()}${modelName.slice(1)}`;

  const StateContext = React.createContext(initialState);
  StateContext.displayName = `${name}StateProvider`;
  const DispatchContext = React.createContext(() => {});
  DispatchContext.displayName = `${name}DispatchProvider`;

  const reducerLogger = (prevState, action) => {
    const nextState = reducer(prevState, action);

    if (process.env.NODE_ENV !== 'test') {
      console.groupCollapsed(
        `${name} | Action: %c${action.type}`,
        'color: white; background-color: #545454;padding: 5px',
      );
      console.log(action);
      console.groupCollapsed(`Before update state:`);
      console.log(prevState);
      console.groupEnd();
      console.groupCollapsed('After update state:');
      console.log(nextState);
      console.groupEnd();
      console.groupEnd();
    }

    return nextState;
  };

  const ReducerProvider = ({ children, initialValue }) => {
    const [state, dispatch] = React.useReducer(
      reducerLogger,
      // If you pass `initialValue` to the provider `initialState` will be ignored
      initialValue || initialState,
    );

    return (
      <DispatchContext.Provider value={dispatch}>
        <StateContext.Provider value={state}>{children}</StateContext.Provider>
      </DispatchContext.Provider>
    );
  };
  ReducerProvider.displayName = `${name}Provider`;
  ReducerProvider.defaultProps = {
    initialValue: undefined,
  };
  ReducerProvider.propTypes = {
    children: PropTypes.any.isRequired,
    initialValue: PropTypes.object,
  };

  function useReducerState() {
    const context = React.useContext(StateContext);
    if (context === undefined) {
      throw new Error(
        `${useReducerState.displayName} must be used within a ${
          ReducerProvider.displayName
        }`,
      );
    }
    return context;
  }
  useReducerState.displayName = `use${name}State`;

  function useReducerDispatch() {
    const context = React.useContext(DispatchContext);
    if (context === undefined) {
      throw new Error(
        `${useReducerDispatch.displayName} must be used within a ${
          ReducerProvider.displayName
        }`,
      );
    }

    return context;
  }
  useReducerDispatch.displayName = `use${name}Dispatch`;

  return {
    ReducerProvider,
    useReducerState,
    useReducerDispatch,
  };
}
